<template>
  <div class="w-full min-h-screen flex flex-col bg-gray-900">
    <MainNav />
    


    <!-- Hero Section with Background Image -->
    <main class="relative h-screen">
      <img 
        src="/image/83illustrationA-Websmall.jpg" 
        alt="Vanuatu beach scene" 
        class="absolute inset-0 w-full h-full object-cover"
      />
      
      <!-- Centered Content -->
      <div class="relative z-30 flex flex-col items-center text-center p-4 h-full pt-20 md:pt-4">
        <img 
          src="/images/83islands_logo.png" 
          alt="83 Islands Distillery Logo" 
          class="w-40 md:w-40 mb-8"
        />
        <img 
          src="/images/83islands-badge.png" 
          alt="83 Islands Distillery Badge" 
          class="w-40 md:w-40 mb-8"
        />
        <div class="flex flex-row justify-center items-start space-x-4 md:space-x-8">
          <div class="vertical-japanese text-white font-bold text-lg sm:text-xl md:text-3xl lg:text-4xl" style="text-shadow: 2px 2px 4px rgba(0,0,0,0.7);">
            <p>一口ごとに</p>
          </div>
          <div class="vertical-japanese text-white font-bold text-lg sm:text-xl md:text-3xl lg:text-4xl" style="text-shadow: 2px 2px 4px rgba(0,0,0,0.7);">
            <p>バヌアツの心が伝わる</p>
          </div>
        </div>
      </div>
    </main>

    <!-- Angled Content Sections -->
    <div class="relative">
      <!-- Yellow Section -->
      <div 
        class="relative bg-[#f1c75d]"
        style="clip-path: polygon(0 15%, 100% 0, 100% 100%, 0 100%); margin-top: -8rem; z-index: 10;">
        <div class="container mx-auto pt-12 pb-48 lg:pt-24 lg:pb-52 px-4">
          <!-- Desktop/Tablet layout (unchanged) -->
          <div class="hidden md:flex flex-row items-center h-full">
            <!-- Left side: Vertical Text -->
            <div class="w-2/5 flex flex-col justify-center items-center space-y-8">
              <!-- Product Name -->
              <h2 class="vertical-japanese text-black font-bold text-4xl sm:text-5xl lg:text-6xl">ユミラム</h2>
              
              <!-- Product Description for Desktop -->
              <div class="text-black text-sm max-w-md leading-relaxed text-center">
                <p class="mb-2 font-medium">ユミラム — 私たちのラム、バヌアツのスピリッツ。</p>
                <p class="mb-2">Yumi Rum（ユミラム）は、スピリッツにとどまりません。土地と人と技、調和を祝福する一杯。</p>
                <p class="mb-2">「Yumi（ユミ）」とは、バヌアツの言葉ビスラマ語で「私たち」という意味です。エファテ島で 50 以上の地元農家の家族によって育てられた在来種のサトウキビは、収穫当日に手作業で圧搾され、フレッシュさと香りを極限まで引き出します。</p>
                <p class="mb-2">ジュースは 7 日間かけて低温発酵させ、ドイツ製 500 リットルのミューラー銅製ポットスチルで蒸留、その結果、透き通るようにクリアで、鮮やかなアグリコール系ラムが生まれます。</p>
                <p>香りと味わいには、青いサトウキビと、シャキッとした青りんごのニュアンスが感じられます。ストレートでも、ロックでも、カクテルでも。</p>
              </div>
            </div>
            <!-- Right side: Bottle Image -->
            <div class="w-3/5 flex justify-center items-center">
              <img src="/image/SC-YRSV-750-YRB2_quarter.png" alt="Yumi Rum Bottle" class="h-auto w-full max-w-[24rem] object-contain">
              <div class="ml-4 pr-12 text-center">
                                                  <div class="text-center">
                  <p class="text-black text-3xl font-bold mb-2">¥6,000</p>
                  <!-- Product Info -->
                  <div v-if="!inventoryLoading && getProductInfo('9354520003487')" class="text-xs text-gray-700 mb-2">
                    <div class="font-medium">
                      {{ getProductInfo('9354520003487').name }}
                      <span v-if="getProductInfo('9354520003487').abv" class="text-gray-600"> • {{ getProductInfo('9354520003487').abv }}% ABV • 750ml</span>
                    </div>
                  </div>
                  <!-- Stock Status -->
                  <div v-if="inventoryLoading" class="text-xs text-gray-500 mb-2">Loading stock...</div>
                  <div v-else-if="!isProductAvailable('9354520003487')" class="text-xs text-red-600 font-medium mb-2">Out of Stock</div>
                  <div v-else-if="getAvailableStock('9354520003487') <= 5" class="text-xs text-orange-600 font-medium mb-2">Low Stock ({{ getAvailableStock('9354520003487') }} left)</div>
                  <div v-else class="text-xs text-green-600 mb-2">In Stock</div>
                  <div class="flex items-center justify-center space-x-2 mb-2">
                    <div class="flex flex-col items-center space-y-1">
                      <button 
                        @click="addToCounter('yumi')"
                        :disabled="!isProductAvailable('9354520003487') || yumiCount >= getAvailableStock('9354520003487')"
                        class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold"
                      >
                        +
                      </button>
                      <button 
                        @click="removeFromCounter('yumi')"
                        :disabled="yumiCount === 0"
                        class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold"
                      >
                        -
                      </button>
                    </div>
                    <span class="text-black font-bold min-w-[20px] text-center text-2xl leading-8">{{ yumiCount }}</span>
                    <button 
                      @click="checkout"
                      :disabled="totalBottles === 0"
                      class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                    >
                      {{ totalBottles === 0 ? 'Add bottles' : `Checkout ${totalBottles} bottles` }}
                    </button>
                  </div>
                  <div class="text-black text-sm">
                    <!-- Cart Breakdown -->
                    <div class="mb-2 space-y-0.5">
                      <div v-if="yumiCount > 0" class="flex justify-between items-center text-xs">
                        <span class="text-black">{{ yumiCount }}x Yumi Rum</span>
                        <span @click="removeAllFromCounter('yumi')" class="text-gray-500 cursor-pointer">remove</span>
                      </div>
                      <div v-if="agedCount > 0" class="flex justify-between items-center text-xs">
                        <span class="text-black">{{ agedCount }}x Aged Rum</span>
                        <span @click="removeAllFromCounter('aged')" class="text-gray-500 cursor-pointer">remove</span>
                      </div>
                      <div v-if="lostTimeCount > 0" class="flex justify-between items-center text-xs">
                        <span class="text-black">{{ lostTimeCount }}x Lost Time Aged Rum</span>
                        <span @click="removeAllFromCounter('lostTime')" class="text-gray-500 cursor-pointer">remove</span>
                      </div>
                    </div>
                    
                    Total: ¥{{ totalPrice.toLocaleString() }}
                    <div class="text-xs text-gray-600 mt-1">送料: ¥2,000 (¥30,000以上で無料)</div>
                    
                    <!-- Email for receipt -->
                    <div v-if="totalBottles > 0" class="mt-3">
                      <input 
                        v-model="customerEmail"
                        type="email" 
                        placeholder="メールアドレス (領収書用)" 
                        class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Mobile layout -->
          <div class="block md:hidden">
            <div class="grid grid-cols-2 gap-4 items-start">
              <div class="flex justify-center items-end min-h-[10rem] pb-4 px-2">
                <h2 class="text-black font-bold text-3xl text-center leading-tight">ユミラム</h2>
              </div>
              <div class="flex justify-center items-end min-h-[10rem]">
                <img src="/image/SC-YRSV-750-YRB2_quarter.png" alt="Yumi Rum Bottle" class="h-auto w-36 max-w-[9rem] object-contain">
              </div>
            </div>
            <div class="mt-4 text-center">
              <p class="text-black text-2xl font-bold mb-2">¥6,000</p>
              <div v-if="!inventoryLoading && getProductInfo('9354520003487')" class="text-xs text-gray-700 mb-2">
                <div class="font-medium">
                  {{ getProductInfo('9354520003487').name }}
                  <span v-if="getProductInfo('9354520003487').abv" class="text-gray-600"> • {{ getProductInfo('9354520003487').abv }}% ABV • 750ml</span>
                </div>
              </div>
              <div v-if="inventoryLoading" class="text-xs text-gray-500 mb-2">Loading stock...</div>
              <div v-else-if="!isProductAvailable('9354520003487')" class="text-xs text-red-600 font-medium mb-2">Out of Stock</div>
              <div v-else-if="getAvailableStock('9354520003487') <= 5" class="text-xs text-orange-600 font-medium mb-2">Low Stock ({{ getAvailableStock('9354520003487') }} left)</div>
              <div v-else class="text-xs text-green-600 mb-2">In Stock</div>
              <div class="flex items-center justify-center space-x-2 mb-2">
                <div class="flex flex-col items-center space-y-1">
                  <button @click="addToCounter('yumi')" :disabled="!isProductAvailable('9354520003487') || yumiCount >= getAvailableStock('9354520003487')" class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold">+</button>
                  <button @click="removeFromCounter('yumi')" :disabled="yumiCount === 0" class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold">-</button>
                </div>
                <span class="text-black font-bold min-w-[20px] text-center text-2xl leading-8">{{ yumiCount }}</span>
                <button @click="checkout" :disabled="totalBottles === 0" class="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm">
                  {{ totalBottles === 0 ? 'Add bottles' : `Checkout ${totalBottles} bottles` }}
                </button>
              </div>
              <div class="text-black text-sm">
                <div class="mb-2 space-y-0.5 flex flex-col items-center">
                  <div v-if="yumiCount > 0" class="inline-flex items-center text-xs space-x-2">
                    <span class="text-black">{{ yumiCount }}x Yumi Rum</span>
                    <span @click="removeAllFromCounter('yumi')" class="text-gray-500 cursor-pointer">remove</span>
                  </div>
                  <div v-if="agedCount > 0" class="inline-flex items-center text-xs space-x-2">
                    <span class="text-black">{{ agedCount }}x Aged Rum</span>
                    <span @click="removeAllFromCounter('aged')" class="text-gray-500 cursor-pointer">remove</span>
                  </div>
                  <div v-if="lostTimeCount > 0" class="inline-flex items-center text-xs space-x-2">
                    <span class="text-black">{{ lostTimeCount }}x Lost Time Aged Rum</span>
                    <span @click="removeAllFromCounter('lostTime')" class="text-gray-500 cursor-pointer">remove</span>
                  </div>
                </div>
                Total: ¥{{ totalPrice.toLocaleString() }}
                <div class="text-xs text-gray-600 mt-1">送料: ¥2,000 (¥30,000以上で無料)</div>
                
                <!-- Email for receipt -->
                <div v-if="totalBottles > 0" class="mt-3">
                  <input 
                    v-model="customerEmail"
                    type="email" 
                    placeholder="メールアドレス (領収書用)" 
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
              </div>
              
              <!-- Product Description for Mobile -->
              <div class="mt-6 text-black text-xs leading-relaxed px-4">
                <p class="mb-2 font-medium">ユミラム — 私たちのラム、バヌアツのスピリッツ。</p>
                <p class="mb-2">Yumi Rum（ユミラム）は、スピリッツにとどまりません。土地と人と技、調和を祝福する一杯。</p>
                <p class="mb-2">「Yumi（ユミ）」とは、バヌアツの言葉ビスラマ語で「私たち」という意味です。エファテ島で 50 以上の地元農家の家族によって育てられた在来種のサトウキビは、収穫当日に手作業で圧搾され、フレッシュさと香りを極限まで引き出します。</p>
                <p class="mb-2">ジュースは 7 日間かけて低温発酵させ、ドイツ製 500 リットルのミューラー銅製ポットスチルで蒸留、その結果、透き通るようにクリアで、鮮やかなアグリコール系ラムが生まれます。</p>
                <p>香りと味わいには、青いサトウキビと、シャキッとした青りんごのニュアンスが感じられます。ストレートでも、ロックでも、カクテルでも。</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Red Section -->
      <div 
        class="relative bg-[#d14e45]" 
        style="clip-path: polygon(0 0, 100% 15%, 100% 100%, 0 100%); margin-top: -10rem; z-index: 11;">
        <div class="container mx-auto pt-12 pb-48 lg:pt-24 lg:pb-52 px-4">
          <!-- Desktop/Tablet layout (unchanged) -->
          <div class="hidden md:flex flex-row items-center h-full">
            <!-- Left side: Bottle Image -->
            <div class="w-3/5 flex justify-center items-center">
              <img src="/image/ARB4-Normal_quarter.png" alt="Aged Rum Bottle" class="h-auto w-full max-w-[24rem] object-contain">
            </div>
            <!-- Right side: Vertical Text and Checkout -->
            <div class="w-2/5 flex flex-col justify-center items-center space-y-8">
              <!-- Product Name and Checkout -->
              <div class="flex flex-row justify-center items-center space-x-8">
                <h2 class="vertical-japanese text-white font-bold text-4xl sm:text-5xl lg:text-6xl">エイジドラム</h2>
                <div class="text-center">
                <p class="text-white text-3xl font-bold mb-2">¥7,000</p>
                <!-- Product Info -->
                <div v-if="!inventoryLoading && getProductInfo('9354520001117')" class="text-xs text-gray-200 mb-2">
                  <div class="font-medium">
                    {{ getProductInfo('9354520001117').name }}
                    <span v-if="getProductInfo('9354520001117').abv" class="text-gray-300"> • {{ getProductInfo('9354520001117').abv }}% ABV • 750ml</span>
                  </div>
                </div>
                <!-- Stock Status -->
                <div v-if="inventoryLoading" class="text-xs text-gray-400 mb-2">Loading stock...</div>
                <div v-else-if="!isProductAvailable('9354520001117')" class="text-xs text-red-400 font-medium mb-2">Out of Stock</div>
                <div v-else-if="getAvailableStock('9354520001117') <= 5" class="text-xs text-orange-400 font-medium mb-2">Low Stock ({{ getAvailableStock('9354520001117') }} left)</div>
                <div v-else class="text-xs text-green-400 mb-2">In Stock</div>
                <div class="flex items-center justify-center space-x-2 mb-2">
                  <div class="flex flex-col items-center space-y-1">
                    <button 
                      @click="addToCounter('aged')"
                      :disabled="!isProductAvailable('9354520001117') || agedCount >= getAvailableStock('9354520001117')"
                      class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold"
                    >
                      +
                    </button>
                    <button 
                      @click="removeFromCounter('aged')"
                      :disabled="agedCount === 0"
                      class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold"
                    >
                      -
                    </button>
                  </div>
                  <span class="text-white font-bold min-w-[20px] text-center text-2xl leading-8">{{ agedCount }}</span>
                  <button 
                    @click="checkout"
                    :disabled="totalBottles === 0"
                    class="bg-white text-black px-4 py-2 rounded-lg hover:bg-gray-100 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                  >
                    {{ totalBottles === 0 ? 'Add bottles' : `Checkout ${totalBottles} bottles` }}
                  </button>
                </div>
                <div class="text-white text-sm">
                  <!-- Cart Breakdown -->
                  <div class="mb-2 space-y-0.5">
                    <div v-if="yumiCount > 0" class="flex justify-between items-center text-xs">
                      <span class="text-white">{{ yumiCount }}x Yumi Rum</span>
                      <span @click="removeAllFromCounter('yumi')" class="text-gray-400 cursor-pointer">remove</span>
                    </div>
                    <div v-if="agedCount > 0" class="flex justify-between items-center text-xs">
                      <span class="text-white">{{ agedCount }}x Aged Rum</span>
                      <span @click="removeAllFromCounter('aged')" class="text-gray-400 cursor-pointer">remove</span>
                    </div>
                    <div v-if="lostTimeCount > 0" class="flex justify-between items-center text-xs">
                      <span class="text-white">{{ lostTimeCount }}x Lost Time Aged Rum</span>
                      <span @click="removeAllFromCounter('lostTime')" class="text-gray-400 cursor-pointer">remove</span>
                    </div>
                  </div>
                  
                  Total: ¥{{ totalPrice.toLocaleString() }}
                  <div class="text-xs text-gray-300 mt-1">送料: ¥2,000 (¥30,000以上で無料)</div>
                  
                  <!-- Email for receipt -->
                  <div v-if="totalBottles > 0" class="mt-3">
                    <input 
                      v-model="customerEmail"
                      type="email" 
                      placeholder="メールアドレス (領収書用)" 
                      class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent bg-white text-black"
                    />
                  </div>
                </div>
                </div>
              </div>
              
              <!-- Product Description for Desktop -->
              <div class="text-white text-sm max-w-md leading-relaxed text-center">
                <p class="mb-2 font-medium">ユミラム 2 年熟成 — 私たちのラム、深く、まろやかに。</p>
                <p class="mb-2">エファテ島で地元の家族たちによって育てられた在来種のサトウキビを使用し、500 リットルのミューラー銅製ポットスチルで蒸留されたユミラム。</p>
                <p class="mb-2">この限定ロットは、ファーストフィルのバーボン樽で 2 年間熟成され、バヌアツのトロピカルな気候によって、さらにまろやかで奥行きのある味わいに仕上がりました。</p>
                <p class="mb-2">キャラメル、トーストしたオーク、バニラ、そして軽く焼いたスパイスの味わい。口当たりは温かく、コクがあり、親しみやすい印象です。ストレートでも、氷をひとかけでもお楽しみいただけます。</p>
                <p>ユミラム 2 年熟成——南の島と樽が育んだ、優雅なひととき。</p>
              </div>
            </div>
          </div>
          <!-- Mobile layout -->
          <div class="block md:hidden">
            <div class="grid grid-cols-2 gap-4 items-start">
              <div class="flex justify-center items-end min-h-[10rem] pb-4 px-2">
                <h2 class="text-white font-bold text-3xl text-center leading-tight">エイジドラム</h2>
              </div>
              <div class="flex justify-center items-end min-h-[10rem]">
                <img src="/image/ARB4-Normal_quarter.png" alt="Aged Rum Bottle" class="h-auto w-36 max-w-[9rem] object-contain">
              </div>
            </div>
            <div class="mt-4 text-center">
              <p class="text-white text-2xl font-bold mb-2">¥7,000</p>
                                <div v-if="!inventoryLoading && getProductInfo('9354520001117')" class="text-xs text-gray-200 mb-2">
                    <div class="font-medium">
                      {{ getProductInfo('9354520001117').name }}
                      <span v-if="getProductInfo('9354520001117').abv" class="text-gray-300"> • {{ getProductInfo('9354520001117').abv }}% ABV • 750ml</span>
                    </div>
                  </div>
              <div v-if="inventoryLoading" class="text-xs text-gray-400 mb-2">Loading stock...</div>
              <div v-else-if="!isProductAvailable('9354520001117')" class="text-xs text-red-400 font-medium mb-2">Out of Stock</div>
              <div v-else-if="getAvailableStock('9354520001117') <= 5" class="text-xs text-orange-400 font-medium mb-2">Low Stock ({{ getAvailableStock('9354520001117') }} left)</div>
              <div v-else class="text-xs text-green-400 mb-2">In Stock</div>
              <div class="flex items-center justify-center space-x-2 mb-2">
                <div class="flex flex-col items-center space-y-1">
                  <button @click="addToCounter('aged')" :disabled="!isProductAvailable('9354520001117') || agedCount >= getAvailableStock('9354520001117')" class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold">+</button>
                  <button @click="removeFromCounter('aged')" :disabled="agedCount === 0" class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold">-</button>
                </div>
                <span class="text-white font-bold min-w-[20px] text-center text-2xl leading-8">{{ agedCount }}</span>
                <button @click="checkout" :disabled="totalBottles === 0" class="bg-white text-black px-4 py-2 rounded-lg hover:bg-gray-100 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm">
                  {{ totalBottles === 0 ? 'Add bottles' : `Checkout ${totalBottles} bottles` }}
                </button>
              </div>
              <div class="text-white text-sm">
                <div class="mb-2 space-y-0.5 flex flex-col items-center">
                  <div v-if="yumiCount > 0" class="inline-flex items-center text-xs space-x-2">
                    <span class="text-white">{{ yumiCount }}x Yumi Rum</span>
                    <span @click="removeAllFromCounter('yumi')" class="text-gray-400 cursor-pointer">remove</span>
                  </div>
                  <div v-if="agedCount > 0" class="inline-flex items-center text-xs space-x-2">
                    <span class="text-white">{{ agedCount }}x Aged Rum</span>
                    <span @click="removeAllFromCounter('aged')" class="text-gray-400 cursor-pointer">remove</span>
                  </div>
                  <div v-if="lostTimeCount > 0" class="inline-flex items-center text-xs space-x-2">
                    <span class="text-white">{{ lostTimeCount }}x Lost Time Aged Rum</span>
                    <span @click="removeAllFromCounter('lostTime')" class="text-gray-400 cursor-pointer">remove</span>
                  </div>
                </div>
                Total: ¥{{ totalPrice.toLocaleString() }}
                <div class="text-xs text-gray-300 mt-1">送料: ¥2,000 (¥30,000以上で無料)</div>
                
                <!-- Email for receipt -->
                <div v-if="totalBottles > 0" class="mt-3">
                  <input 
                    v-model="customerEmail"
                    type="email" 
                    placeholder="メールアドレス (領収書用)" 
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent bg-white text-black"
                  />
                </div>
              </div>
              
              <!-- Product Description for Mobile -->
              <div class="mt-6 text-white text-xs leading-relaxed px-4">
                <p class="mb-2 font-medium">ユミラム 2 年熟成 — 私たちのラム、深く、まろやかに。</p>
                <p class="mb-2">エファテ島で地元の家族たちによって育てられた在来種のサトウキビを使用し、500 リットルのミューラー銅製ポットスチルで蒸留されたユミラム。</p>
                <p class="mb-2">この限定ロットは、ファーストフィルのバーボン樽で 2 年間熟成され、バヌアツのトロピカルな気候によって、さらにまろやかで奥行きのある味わいに仕上がりました。</p>
                <p class="mb-2">キャラメル、トーストしたオーク、バニラ、そして軽く焼いたスパイスの味わい。口当たりは温かく、コクがあり、親しみやすい印象です。ストレートでも、氷をひとかけでもお楽しみいただけます。</p>
                <p>ユミラム 2 年熟成——南の島と樽が育んだ、優雅なひととき。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Brown Section -->
      <div 
        class="relative bg-[#7a5231]"
        style="clip-path: polygon(0 15%, 100% 0, 100% 100%, 0 100%); margin-top: -10rem; z-index: 12;">
        <div class="container mx-auto pt-12 pb-24 lg:pt-16 lg:pb-32 px-4">
          <!-- Desktop/Tablet layout (unchanged) -->
          <div class="hidden md:flex flex-row items-center h-full">
            <!-- Left side: Vertical Text -->
            <div class="w-2/5 flex flex-col justify-center items-center space-y-8">
              <!-- Product Name -->
              <h2 class="vertical-japanese text-white font-bold text-4xl sm:text-5xl lg:text-6xl">エイジドラム<br>「ロストタイム」</h2>
              
              <!-- Product Description for Desktop -->
              <div class="text-white text-sm max-w-md leading-relaxed text-center">
                <p class="mb-2 font-medium">ロストタイムズ — 大地と記憶が育んだラム</p>
                <p class="mb-2">2023 年 12 月 17 日、バヌアツを襲った大地震。私たちの蒸留所も大きな揺れに見舞われ、熟成中の樽のいくつかが崩れ落ちました。失われたものもあれば、奇跡的に残ったものも。</p>
                <p class="mb-2">「ロストタイムズ」は、その生き残った 7 つの樽から生まれた特別なブレンドです。ブランデー樽、ライウイスキー樽、フレンチバリック、新樽、ラム樽——それぞれが個性を放ち、重なり合うことで、複雑で芯のある味わいを生み出しました。</p>
                <p class="mb-2">この一本は、失われた時間への敬意であり、今なお続く物語への讃歌でもあります。</p>
                <p>ごく少量のみの限定リリース。</p>
              </div>
            </div>
            <!-- Right side: Bottle Image -->
            <div class="w-3/5 flex justify-center items-center">
              <img src="/image/ARB10_quarter.png" alt="Lost Time Aged Rum Bottle Placeholder" class="h-auto w-full max-w-[24rem] object-contain">
              <div class="ml-4 pr-12 text-center">
                <div class="text-center">
                  <p class="text-white text-3xl font-bold mb-2">¥8,000</p>
                  <!-- Product Info -->
                  <div v-if="!inventoryLoading && getProductInfo('9354520004798')" class="text-xs text-gray-200 mb-2">
                    <div class="font-medium">
                      {{ getProductInfo('9354520004798').name }}
                      <span v-if="getProductInfo('9354520004798').abv" class="text-gray-300"> • {{ getProductInfo('9354520004798').abv }}% ABV • 750ml</span>
                    </div>
                  </div>
                  <!-- Stock Status -->
                  <div v-if="inventoryLoading" class="text-xs text-gray-400 mb-2">Loading stock...</div>
                  <div v-else-if="!isProductAvailable('9354520004798')" class="text-xs text-red-400 font-medium mb-2">Out of Stock</div>
                  <div v-else-if="getAvailableStock('9354520004798') <= 5" class="text-xs text-orange-400 font-medium mb-2">Low Stock ({{ getAvailableStock('9354520004798') }} left)</div>
                  <div v-else class="text-xs text-green-400 mb-2">In Stock</div>
                  <div class="flex items-center justify-center space-x-2 mb-2">
                    <div class="flex flex-col items-center space-y-1">
                      <button 
                        @click="addToCounter('lostTime')"
                        :disabled="!isProductAvailable('9354520004798') || lostTimeCount >= getAvailableStock('9354520004798')"
                        class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold"
                      >
                        +
                      </button>
                      <button 
                        @click="removeFromCounter('lostTime')"
                        :disabled="lostTimeCount === 0"
                        class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold"
                      >
                        -
                      </button>
                    </div>
                    <span class="text-white font-bold min-w-[20px] text-center text-2xl leading-8">{{ lostTimeCount }}</span>
                    <button 
                      @click="checkout"
                      :disabled="totalBottles === 0"
                      class="bg-white text-black px-4 py-2 rounded-lg hover:bg-gray-100 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                    >
                      {{ totalBottles === 0 ? 'Add bottles' : `Checkout ${totalBottles} bottles` }}
                  </button>
                  </div>
                  <div class="text-white text-sm">
                    <!-- Cart Breakdown -->
                    <div class="mb-2 space-y-0.5">
                      <div v-if="yumiCount > 0" class="flex justify-between items-center text-xs">
                        <span class="text-white">{{ yumiCount }}x Yumi Rum</span>
                        <span @click="removeAllFromCounter('yumi')" class="text-gray-400 cursor-pointer">remove</span>
                      </div>
                      <div v-if="agedCount > 0" class="flex justify-between items-center text-xs">
                        <span class="text-white">{{ agedCount }}x Aged Rum</span>
                        <span @click="removeAllFromCounter('aged')" class="text-gray-400 cursor-pointer">remove</span>
                      </div>
                      <div v-if="lostTimeCount > 0" class="flex justify-between items-center text-xs">
                        <span class="text-white">{{ lostTimeCount }}x Lost Time Aged Rum</span>
                        <span @click="removeAllFromCounter('lostTime')" class="text-gray-400 cursor-pointer">remove</span>
                      </div>
                    </div>
                    
                    Total: ¥{{ totalPrice.toLocaleString() }}
                    <div class="text-xs text-gray-300 mt-1">送料: ¥2,000 (¥30,000以上で無料)</div>
                    
                    <!-- Email for receipt -->
                    <div v-if="totalBottles > 0" class="mt-3">
                      <input 
                        v-model="customerEmail"
                        type="email" 
                        placeholder="メールアドレス (領収書用)" 
                        class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent bg-white text-black"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Mobile layout -->
          <div class="block md:hidden">
            <div class="grid grid-cols-2 gap-4 items-start">
              <div class="flex justify-center items-end min-h-[10rem] pb-4 px-2">
                <h2 class="text-white font-bold text-2xl text-center leading-tight">エイジドラム<br>「ロストタイム」</h2>
              </div>
              <div class="flex justify-center items-end min-h-[10rem]">
                <img src="/image/ARB10_quarter.png" alt="Lost Time Aged Rum Bottle" class="h-auto w-36 max-w-[9rem] object-contain">
              </div>
            </div>
            <div class="mt-4 text-center">
              <p class="text-white text-2xl font-bold mb-2">¥8,000</p>
              <div v-if="!inventoryLoading && getProductInfo('9354520004798')" class="text-xs text-gray-200 mb-2">
                <div class="font-medium">
                  {{ getProductInfo('9354520004798').name }}
                  <span v-if="getProductInfo('9354520004798').abv" class="text-gray-300"> • {{ getProductInfo('9354520004798').abv }}% ABV • 750ml</span>
                </div>
              </div>
              <div v-if="inventoryLoading" class="text-xs text-gray-400 mb-2">Loading stock...</div>
              <div v-else-if="!isProductAvailable('9354520004798')" class="text-xs text-red-400 font-medium mb-2">Out of Stock</div>
              <div v-else-if="getAvailableStock('9354520004798') <= 5" class="text-xs text-orange-400 font-medium mb-2">Low Stock ({{ getAvailableStock('9354520004798') }} left)</div>
              <div v-else class="text-xs text-green-400 mb-2">In Stock</div>
              <div class="flex items-center justify-center space-x-2 mb-2">
                <div class="flex flex-col items-center space-y-1">
                  <button @click="addToCounter('lostTime')" :disabled="!isProductAvailable('9354520004798') || lostTimeCount >= getAvailableStock('9354520004798')" class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold">+</button>
                  <button @click="removeFromCounter('lostTime')" :disabled="lostTimeCount === 0" class="bg-gray-200 text-black w-8 h-8 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg font-bold">-</button>
                </div>
                <span class="text-white font-bold min-w-[20px] text-center text-2xl leading-8">{{ lostTimeCount }}</span>
                <button @click="checkout" :disabled="totalBottles === 0" class="bg-white text-black px-4 py-2 rounded-lg hover:bg-gray-100 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm">
                  {{ totalBottles === 0 ? 'Add bottles' : `Checkout ${totalBottles} bottles` }}
                </button>
              </div>
              <div class="text-white text-sm">
                <div class="mb-2 space-y-0.5 flex flex-col items-center">
                  <div v-if="yumiCount > 0" class="inline-flex items-center text-xs space-x-2">
                    <span class="text-white">{{ yumiCount }}x Yumi Rum</span>
                    <span @click="removeAllFromCounter('yumi')" class="text-gray-400 cursor-pointer">remove</span>
                  </div>
                  <div v-if="agedCount > 0" class="inline-flex items-center text-xs space-x-2">
                    <span class="text-white">{{ agedCount }}x Aged Rum</span>
                    <span @click="removeAllFromCounter('aged')" class="text-gray-400 cursor-pointer">remove</span>
                  </div>
                  <div v-if="lostTimeCount > 0" class="inline-flex items-center text-xs space-x-2">
                    <span class="text-white">{{ lostTimeCount }}x Lost Time Aged Rum</span>
                    <span @click="removeAllFromCounter('lostTime')" class="text-gray-400 cursor-pointer">remove</span>
                  </div>
                </div>
                                Total: ¥{{ totalPrice.toLocaleString() }}
                <div class="text-xs text-gray-300 mt-1">送料: ¥2,000 (¥30,000以上で無料)</div>
                
                <!-- Email for receipt -->
                <div v-if="totalBottles > 0" class="mt-3">
                  <input 
                    v-model="customerEmail"
                    type="email" 
                    placeholder="メールアドレス (領収書用)" 
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent bg-white text-black"
                  />
                </div>
              </div>
              
              <!-- Product Description for Mobile -->
              <div class="mt-6 text-white text-xs leading-relaxed px-4">
                <p class="mb-2 font-medium">ロストタイムズ — 大地と記憶が育んだラム</p>
                <p class="mb-2">2023 年 12 月 17 日、バヌアツを襲った大地震。私たちの蒸留所も大きな揺れに見舞われ、熟成中の樽のいくつかが崩れ落ちました。失われたものもあれば、奇跡的に残ったものも。</p>
                <p class="mb-2">「ロストタイムズ」は、その生き残った 7 つの樽から生まれた特別なブレンドです。ブランデー樽、ライウイスキー樽、フレンチバリック、新樽、ラム樽——それぞれが個性を放ち、重なり合うことで、複雑で芯のある味わいを生み出しました。</p>
                <p class="mb-2">この一本は、失われた時間への敬意であり、今なお続く物語への讃歌でもあります。</p>
                <p>ごく少量のみの限定リリース。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      


      

      <!-- White Video Section -->
      <div class="relative bg-white pt-24 pb-32">
        <div class="container mx-auto px-4">
          <div class="aspect-w-16 aspect-h-9 mb-12">
            <iframe 
              src="https://www.youtube.com/embed/_ZL6fclsB2I" 
              frameborder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen
              class="w-full h-full">
            </iframe>
          </div>
          <div class="aspect-w-16 aspect-h-9 mb-12">
            <iframe 
              src="https://youtube.com/embed/wc41vRIxK7o" 
              frameborder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen
              class="w-full h-full">
            </iframe>
          </div>
 
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import MainNav from '../components/MainNav.vue';
import Footer from '../components/Footer.vue';

// Individual product counters
const yumiCount = ref(0);
const agedCount = ref(0);
const lostTimeCount = ref(0);

// Customer email for receipt
const customerEmail = ref('');

// Inventory state
const inventory = ref({});
const inventoryLoading = ref(false);

// Computed properties for totals
const totalBottles = computed(() => {
  return yumiCount.value + agedCount.value + lostTimeCount.value;
});

const totalPrice = computed(() => {
  return (yumiCount.value * 6000) + (agedCount.value * 7000) + (lostTimeCount.value * 8000);
});

// Counter functions
const addToCounter = (productId) => {
  let sku, currentCount;
  
  switch (productId) {
    case 'yumi':
      sku = '9354520003487';
      currentCount = yumiCount.value;
      break;
    case 'aged':
      sku = '9354520001117';
      currentCount = agedCount.value;
      break;
    case 'lostTime':
      sku = '9354520004798';
      currentCount = lostTimeCount.value;
      break;
  }
  
  const availableStock = getAvailableStock(sku);
  
  if (currentCount >= availableStock) {
    alert(`Sorry, only ${availableStock} bottles available in stock.`);
    return;
  }
  
  switch (productId) {
    case 'yumi':
      yumiCount.value += 1;
      break;
    case 'aged':
      agedCount.value += 1;
      break;
    case 'lostTime':
      lostTimeCount.value += 1;
      break;
  }
};

const removeFromCounter = (productId) => {
  switch (productId) {
    case 'yumi':
      if (yumiCount.value > 0) yumiCount.value -= 1;
      break;
    case 'aged':
      if (agedCount.value > 0) agedCount.value -= 1;
      break;
    case 'lostTime':
      if (lostTimeCount.value > 0) lostTimeCount.value -= 1;
      break;
  }
};

const removeAllFromCounter = (productId) => {
  switch (productId) {
    case 'yumi':
      yumiCount.value = 0;
      break;
    case 'aged':
      agedCount.value = 0;
      break;
    case 'lostTime':
      lostTimeCount.value = 0;
      break;
  }
};

// Load inventory
const loadInventory = async () => {
  inventoryLoading.value = true;
  try {
    const response = await fetch('/api/inventory');
    const data = await response.json();
    
    if (data.success) {
      // Convert array to object for easier access
      const inventoryObj = {};
      data.inventory.forEach(item => {
        inventoryObj[item.productId] = item;
      });
      inventory.value = inventoryObj;
    }
  } catch (error) {
    console.error('Error loading inventory:', error);
  } finally {
    inventoryLoading.value = false;
  }
};

// Check if product is available
const isProductAvailable = (productId) => {
  const product = inventory.value[productId];
  return product && product.quantity > 0;
};

// Get available stock
const getAvailableStock = (productId) => {
  const product = inventory.value[productId];
  return product ? product.quantity : 0;
};

// Get product info (name and ABV)
const getProductInfo = (productId) => {
  const product = inventory.value[productId];
  return product ? {
    name: product.name || productId,
    abv: product.abv
  } : null;
};

const checkout = async () => {
  if (totalBottles.value === 0) return;
  
  try {
    // Create cart items array
    const cartItems = [];
    if (yumiCount.value > 0) {
      cartItems.push({ id: '9354520003487', quantity: yumiCount.value });
    }
    if (agedCount.value > 0) {
      cartItems.push({ id: '9354520001117', quantity: agedCount.value });
    }
    if (lostTimeCount.value > 0) {
      cartItems.push({ id: '9354520004798', quantity: lostTimeCount.value });
    }

    // Calculate shipping
    const shippingCost = totalPrice.value >= 30000 ? 0 : 2000;
    const finalTotal = totalPrice.value + shippingCost;

    // Create Stripe Checkout session
    const response = await fetch('/api/stripe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        cartItems,
        email: customerEmail.value, // Send email if provided
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/jp`,
      }),
    });

    const data = await response.json();
    
    if (data.success && data.url) {
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } else {
      console.error('Failed to create checkout session:', data.error);
      alert(`Checkout failed: ${data.error || 'Please try again.'}`);
    }
  } catch (error) {
    console.error('Checkout error:', error);
    if (error.message.includes('JSON')) {
      alert('Checkout service is not available. Please check your Stripe configuration.');
    } else {
      alert(`Checkout failed: ${error.message || 'Please try again.'}`);
    }
  }
};

// Load inventory on component mount
onMounted(() => {
  loadInventory();
});

</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@700&display=swap');

.vertical-japanese {
  font-family: 'Noto Sans JP', sans-serif;
  writing-mode: vertical-rl;
}

.aspect-w-16 {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
}

.aspect-w-16 > iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  }
</style> 